// @ts-check

/**
 * Mapbox event handler utilities.
 * Provides functions for setting up map interaction handlers (click, hover, etc.).
 */

/**
 * Setup click handler for island markers.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {string} layerId - Layer ID to attach handler to
 * @param {Function} onIslandClick - Callback when island is clicked
 */
export function setupIslandClickHandler(map, layerId, onIslandClick) {
  map.on("click", layerId, (event) => {
    const feature = event.features[0];
    onIslandClick(feature);
  });
}

/**
 * Setup hover handlers for island markers (cursor only).
 * Labels are handled by Mapbox's native symbol layer, so we only need cursor changes here.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {string} layerId - Layer ID to attach handlers to
 * @returns {Object} Object with cleanup function
 */
export function setupIslandHoverHandlers(map, layerId) {
  // Change cursor on hover
  map.on("mouseenter", layerId, () => {
    map.getCanvas().style.cursor = "pointer";
  });

  // Reset cursor on leave
  map.on("mouseleave", layerId, () => {
    map.getCanvas().style.cursor = "";
  });

  return {
    cleanup: () => {
      // No cleanup needed for simple cursor handlers
    }
  };
}

/**
 * Setup click handler for cultural region polygons.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {string} layerId - Layer ID to attach handler to
 * @param {Function} onRegionClick - Callback when region is clicked
 */
export function setupRegionClickHandler(map, layerId, onRegionClick) {
  map.on("click", layerId, (event) => {
    const region = event.features[0].properties;
    console.log("🌍 REGION CLICK DEBUG:", {
      regionProperties: region,
      clickedPoint: event.point,
      lngLat: event.lngLat,
      regionName:
        region?.name ||
        region?.["Region Name"] ||
        region?.place_name ||
        "Unknown",
      regionType: region?.type || "region"
    });
    onRegionClick(region);
  });
}

/**
 * Setup hover handlers for cultural region polygons (cursor only).
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {string} layerId - Layer ID to attach handlers to
 */
export function setupRegionHoverHandlers(map, layerId) {
  map.on("mouseenter", layerId, () => {
    map.getCanvas().style.cursor = "pointer";
  });

  map.on("mouseleave", layerId, () => {
    map.getCanvas().style.cursor = "";
  });
}

/**
 * Setup click handler for migration routes.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {string} layerId - Layer ID to attach handler to
 * @param {Function} onRouteClick - Callback when route is clicked
 */
export function setupRouteClickHandler(map, layerId, onRouteClick) {
  map.on("click", layerId, (event) => {
    const route = event.features[0].properties;
    console.log("🛤️ ROUTE CLICK DEBUG:", {
      routeProperties: route,
      clickedPoint: event.point,
      lngLat: event.lngLat,
      routeName:
        route?.name || route?.description || route?.["Route ID"] || "Unknown",
      routeType: route?.type || "route"
    });
    onRouteClick(route);
  });
}
